import { MiddlewareState, Placement, Strategy } from '@floating-ui/core'
import { FlipOptions } from '@floating-ui/dom'
import {
  autoUpdate,
  detectOverflow,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { WORK_AREA_ID } from 'client/constants/editorIds'
import React, { useState } from 'react'

const overflowMiddleWare = {
  name: 'overflowMiddleWare',
  async fn(state: MiddlewareState) {
    const overflow = await detectOverflow(state, {
      boundary: document.querySelector(`#${WORK_AREA_ID}`) || undefined,
    })
    if (overflow.top > 0) {
      return {
        y: state.y + overflow.top,
      }
    }
    return {}
  },
}

interface usePopoverPropertiesProps {
  offsetValue: number
  flipOptions: FlipOptions
  strategyValue: Strategy
  placement: Placement
  ignoredEventTypes?: Event['type'][]
  isOpen?: boolean
  setIsOpen?: (isOpen: boolean) => void
}

const usePopoverProperties = ({
  offsetValue,
  flipOptions,
  strategyValue,
  placement,
  ignoredEventTypes = [],
  isOpen: externalIsOpen,
  setIsOpen: externalSetIsOpen,
}: usePopoverPropertiesProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false)

  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen
  const setIsOpen = externalSetIsOpen || setInternalIsOpen

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open, event) => {
      // either explicit call (no event) or allowed event type
      if (!(event && ignoredEventTypes.includes(event.type))) {
        setIsOpen(open)
      }
    },
    placement: placement,
    middleware: [
      offset(offsetValue),
      flip(flipOptions),
      shift(),
      overflowMiddleWare,
    ],
    strategy: strategyValue,
    whileElementsMounted: autoUpdate,
  })
  const click = useClick(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ])

  return React.useMemo(
    () => ({
      refs,
      floatingStyles: { ...floatingStyles, zIndex: 100 },
      getReferenceProps,
      getFloatingProps,
      isOpen,
      context,
    }),
    [
      refs,
      floatingStyles,
      getReferenceProps,
      getFloatingProps,
      isOpen,
      context,
    ],
  )
}

export default usePopoverProperties
